/* ============================================================================
   02 · Pinned fabric story
   ----------------------------------------------------------------------------
   Pattern: pin a full-height section and advance through N panels as the user
   scrolls past it. The workhorse of editorial commerce pages.

   What's being decided here:
     • Scroll budget per panel. `.story` is 320vh for three panels. Under ~100vh
       each and panels get skipped by a single trackpad flick; over ~140vh and
       people think the page is stuck.
     • Whether panels cross-fade or hard-cut. Currently a short overlapping
       cross-fade with the swatch counter-moving — a hard cut is cheaper and
       reads as a slideshow, which is the thing to avoid.
     • The copy stagger inside each panel. Set generous here because each panel
       gets its own attention; a grid would use stagger.tight instead.

   Accessibility note: pinning is fully disabled under reduced motion. The
   panels stack and read as three normal sections — not a fast version of the
   same scroll hijack, which still causes nausea.
   ========================================================================= */

import { initMotion, refreshOnSettle, isReduced, MOTION } from '../../src/motion.js';
import { mountLabBar } from '../../src/lab.js';

const { gsap, ScrollTrigger } = initMotion();
mountLabBar({ title: '02 · Pinned story' });
refreshOnSettle();

const panels = gsap.utils.toArray('[data-panel]');
const dots = gsap.utils.toArray('[data-dot]');
let master;

function setActiveDot(index) {
  dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
}

function buildStatic() {
  // Reduced-motion fallback: unpin, stack, show everything. Same content, no
  // scroll hijack and no timing dependency.
  gsap.set('.story', { height: 'auto' });
  gsap.set('.story__stage', { height: 'auto', overflow: 'visible' });
  gsap.set('.story__panels', { height: 'auto' });
  gsap.set(panels, {
    position: 'relative',
    opacity: 1,
    clearProps: 'transform',
    minHeight: '100svh',
  });
  gsap.set('[data-panel-line]', { opacity: 1, y: 0 });
  gsap.set('[data-swatch]', { opacity: 1, scale: 1, yPercent: 0 });
  setActiveDot(0);
}

function buildPinned() {
  gsap.set('.story', { clearProps: 'height' });
  gsap.set('.story__stage', { clearProps: 'height,overflow' });
  gsap.set('.story__panels', { clearProps: 'height' });
  gsap.set(panels, { clearProps: 'position,minHeight' });

  // Start state: first panel live, the rest parked below with their copy down.
  panels.forEach((panel, i) => {
    gsap.set(panel, { opacity: i === 0 ? 1 : 0 });
    gsap.set(panel.querySelectorAll('[data-panel-line]'), {
      opacity: i === 0 ? 1 : 0,
      y: i === 0 ? 0 : MOTION.rise.md,
    });
    gsap.set(panel.querySelector('[data-swatch]'), {
      scale: i === 0 ? 1 : 1.08,
      yPercent: i === 0 ? 0 : 6,
    });
  });
  setActiveDot(0);

  master = gsap.timeline({
    scrollTrigger: {
      trigger: '.story',
      start: 'top top',
      end: 'bottom bottom',
      pin: '.story__stage',
      pinSpacing: false, // the 320vh wrapper already reserves the space
      scrub: 0.6, // slight lag smooths trackpad jitter without feeling laggy
      onUpdate: (self) => {
        gsap.set('[data-progress]', { scaleX: self.progress });
        setActiveDot(Math.min(panels.length - 1, Math.floor(self.progress * panels.length)));
      },
    },
  });

  // One segment per transition, not per panel — N panels means N-1 handoffs.
  panels.slice(1).forEach((panel, i) => {
    const previous = panels[i];
    const at = i; // timeline units; scrub maps scroll distance onto these

    master
      .to(previous, { opacity: 0, ease: MOTION.ease.standard, duration: 0.6 }, at)
      .to(
        previous.querySelectorAll('[data-panel-line]'),
        { opacity: 0, y: -MOTION.rise.sm, ease: MOTION.ease.standard, duration: 0.5 },
        at
      )
      .to(
        previous.querySelector('[data-swatch]'),
        { yPercent: -8, scale: 0.96, ease: MOTION.ease.standard, duration: 0.7 },
        at
      )
      // Incoming overlaps the outgoing by ~0.2 — that overlap is what makes it
      // a transition rather than a slideshow advance.
      .to(panel, { opacity: 1, ease: MOTION.ease.standard, duration: 0.6 }, at + 0.2)
      .to(
        panel.querySelector('[data-swatch]'),
        { yPercent: 0, scale: 1, ease: MOTION.ease.entrance, duration: 0.8 },
        at + 0.2
      )
      .to(
        panel.querySelectorAll('[data-panel-line]'),
        {
          opacity: 1,
          y: 0,
          ease: MOTION.ease.entrance,
          duration: 0.7,
          stagger: MOTION.stagger.loose,
        },
        at + 0.3
      );
  });
}

function build() {
  master?.kill();
  ScrollTrigger?.getAll().forEach((t) => t.kill());
  if (isReduced()) buildStatic();
  else buildPinned();
  ScrollTrigger?.refresh();
}

// Chapter dots jump scroll position rather than seeking the timeline — seeking
// a scrubbed timeline desyncs it from the scrollbar and the next wheel event
// snaps back.
dots.forEach((dot, i) => {
  dot.addEventListener('click', () => {
    const trigger = ScrollTrigger?.getAll().find((t) => t.pin);
    if (!trigger) {
      panels[i].scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const target = trigger.start + (trigger.end - trigger.start) * (i / panels.length) + 8;
    window.scrollTo({ top: target, behavior: 'smooth' });
  });
});

build();
window.addEventListener('mo:replay', build);
